// src/das-application/application-steps/StepContent1.js
import React, { memo } from "react";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import FormRow from "../../helpers/FormRow";
import FormInput from "../../helpers/FormInput";
import FormSelect from "../../helpers/FormSelect";
import FormRadioGroup from "../../helpers/FormRadioGroup";
import FormCheckbox from "../../helpers/FormCheckbox";

function StepContent1({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
}) {
  // console.log("data", data);
  const getStatus = (field, isRequired) => {
    if (stepErrors[field]) {
      return "error";
    }
    if (isRequired && formData[field]) {
      return "success";
    }
    if (!isRequired && !formData[field]) {
      return "warning";
    }
    return "";
  };

  return (
    <div className="form-group">
      <h3 className="bold-text">Personal Information:</h3>
      <FormRow spacing={2}>
        <Grid item xs={12}>
          <FormInput
            label="Legal Name"
            id="legalName"
            value={formData.legal_name}
            onChange={handleChange("legal_name")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.legal_name)}
            helperText={
              stepErrors.legal_name ||
              "Please enter your full legal name (at least two words)."
            }
            status={getStatus("legal_name", true)}
          />
        </Grid>
      </FormRow>

      <FormRow spacing={2}>
        <Grid item xs={12} md={6}>
          <FormRadioGroup
            label="Citizenship"
            name="citizenship"
            value={formData.citizenship}
            onChange={handleChange("citizenship")}
            options={[
              { value: "Canadian Citizen", label: "Canadian Citizen" },
              { value: "Permanent Resident", label: "Permanent Resident" },
            ]}
            required
            row
            isReadOnly={isReadOnly}
            error={Boolean(stepErrors.citizenship)}
            helperText={stepErrors.citizenship || ""}
            status={getStatus("citizenship", true)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormInput
            label="SIN Number"
            id="SIN"
            value={formData.SIN}
            onChange={handleChange("SIN")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.SIN)}
            helperText={stepErrors.SIN || ""}
            status={getStatus("SIN", true)}
          />
        </Grid>
      </FormRow>

      <FormRow spacing={2}>
        <Grid item xs={12} md={6}>
          <FormSelect
            label="School"
            id="school_id"
            value={formData.school_id}
            onChange={handleChange("school_id")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.school_id)}
            helperText={stepErrors.school_id || ""}
            status={getStatus("school_id", true)}
          >
            <MenuItem value="">
              <em>Select School</em>
            </MenuItem>
            <MenuItem value="1">Alternate</MenuItem>
            <MenuItem value="12">Cedar</MenuItem>
            <MenuItem value="4">Dover Bay Secondary</MenuItem>
            <MenuItem value="5">IslandConnectED</MenuItem>
            <MenuItem value="2">John Barsby Community Secondary</MenuItem>
            <MenuItem value="6">Ladysmith Secondary</MenuItem>
            <MenuItem value="7">Nanaimo District Secondary</MenuItem>
            <MenuItem value="9">Wellington Secondary</MenuItem>
          </FormSelect>
        </Grid>
        <Grid item xs={12} md={12}>
          <FormCheckbox
            label="Check if you are in the CTC program:"
            checked={formData?.CTC}
            onChange={handleChange("CTC")}
            name="ctc"
            status=""
            displaySide="left"
            isReadOnly={isReadOnly}
          />
        </Grid>
      </FormRow>

      <h3 className="bold-text">Address Details:</h3>
      <FormRow spacing={2}>
        <Grid item xs={12} md={8}>
          <FormInput
            label="Mailing Address"
            id="address"
            value={formData.address}
            onChange={handleChange("address")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.address)}
            helperText={stepErrors.address || ""}
            status={getStatus("address", true)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormInput
            label="City"
            id="city"
            value={formData.city}
            onChange={handleChange("city")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.city)}
            helperText={stepErrors.city || ""}
            status={getStatus("city", true)}
          />
        </Grid>
      </FormRow>
      <FormRow spacing={2}>
        <Grid item xs={12} md={4}>
          <FormInput
            label="Province"
            id="province"
            value={formData.province}
            onChange={handleChange("province")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.province)}
            helperText={stepErrors.province || ""}
            status={getStatus("province", true)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormInput
            label="Postal Code"
            id="postalCode"
            value={formData.postalCode}
            onChange={handleChange("postalCode")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.postalCode)}
            helperText={stepErrors.postalCode || ""}
            status={getStatus("postalCode", true)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormSelect
            label="Country"
            id="country"
            value={formData.country}
            onChange={handleChange("country")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.country)}
            helperText={stepErrors.country || ""}
            status={getStatus("country", true)}
          >
            <MenuItem value="Canada">Canada</MenuItem>
            <MenuItem value="United States">United States</MenuItem>
          </FormSelect>
        </Grid>
      </FormRow>

      <FormRow spacing={2}>
        <Grid item xs={12} md={4}>
          <FormInput
            label="Cell Phone"
            id="cellPhone"
            value={formData.cellPhone}
            onChange={handleChange("cellPhone")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.cellPhone)}
            helperText={stepErrors.cellPhone || ""}
            status={getStatus("cellPhone", true)}
          />
        </Grid>
      </FormRow>

      <FormRow spacing={2}>
        <Grid item xs={12} md={6}>
          <FormInput
            label="Date of Birth"
            id="dob"
            type="date"
            value={formData.dob}
            onChange={handleChange("dob")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.dob)}
            helperText={
              stepErrors.dob || "Age must be between 14 and 22 years old."
            }
            status={getStatus("dob", true)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormInput
            label="SD68 Student PEN Number (9 digits)"
            id="PEN"
            value={formData.PEN}
            onChange={handleChange("PEN")}
            required
            disabled={isReadOnly}
            error={Boolean(stepErrors.PEN)}
            helperText={stepErrors.PEN || ""}
            status={getStatus("PEN", true)}
          />
        </Grid>
      </FormRow>
    </div>
  );
}

export default memo(StepContent1);
